<template>
    <div class="container request-campaign-form-view">
        <h2 class="request-campaign-form-view__title" v-if="!formSubmitted">
            Campaign Request Form
        </h2>

        <div class="request-campaign-form-view__content">
            <BaseCard v-if="!formSubmitted" class="main-form-card">
                <template #content>
                    <div v-if="isFirstStep" class="campaign-form-container">
                        <RequestCampaignStartFrom @startFrom="setStartFrom" />
                    </div>

                    <div class="campaign-form" v-else>
                        <RequestCampaignFormHeader
                            v-if="isFlagEnabled"
                            :startFrom="startFrom"
                            @previousStep="handlePreviousStep"
                        />

                        <div
                            v-if="startFrom === 'template' && !form.template.id"
                            class="campaign-form-container"
                        >
                            <TemplateSearch
                                :bindings="bindings"
                                @templateSelected="handleTemplateSelected"
                            />
                        </div>

                        <div
                            v-else-if="startFrom === 'past_campaign' && !form.template.id"
                            class="campaign-form-container"
                        >
                            <PastCampaignSearch
                                :bindings="bindings"
                                @pastCampaignSelected="handlePastCampaignSelected"
                            />
                        </div>

                        <div class="campaign-form-container" v-else>
                            <div class="info-panel">
                                <b class="blue-bold">Important Reminder:</b> Please submit your
                                campaign request at least 48 hours prior to your desired send date
                                to allow our team time to process your request and resolve any
                                outstanding questions.
                            </div>

                            <div class="campaign-form-item">
                                <BaseInputText
                                    v-model="form.template.name"
                                    label="Campaign Name*"
                                    placeholder="Enter campaign name"
                                    :invalid="
                                        !form.template.name || form.template.name.length === 0
                                    "
                                />
                            </div>

                            <div class="campaign-form-item">
                                <div
                                    v-if="startFrom === 'template' && chosenTemplate"
                                    class="campaign-form-item"
                                >
                                    <span>Message submission*</span>
                                    <TemplateCard :bindings="bindings" :template="chosenTemplate" />
                                </div>
                                <div
                                    v-else-if="startFrom === 'past_campaign' && chosenCampaign"
                                    class="campaign-form-item"
                                >
                                    <span>Message submission*</span>
                                    <PastCampaignCard
                                        :bindings="bindings"
                                        :campaign="chosenCampaign"
                                    />
                                </div>
                                <BaseTextarea
                                    v-else
                                    v-model="form.firstQuestion.text"
                                    label="Message Submission*"
                                    placeholder="Enter message text and explain any relevant logic here"
                                    :invalid="
                                        !form.firstQuestion.text ||
                                        form.firstQuestion.text.length === 0
                                    "
                                />
                            </div>

                            <div class="campaign-form-item">
                                <BaseSelect
                                    v-model="typeOfStudents"
                                    label="Who Would You Like to Send This Message to?*"
                                    placeholder="Select a student filter"
                                    :options="options"
                                    :invalid="!typeOfStudents"
                                />

                                <div class="campaign-form-subitem">
                                    <div v-if="typeOfStudents === 'all'" class="info-panel">
                                        If you select "All Students" we will message all students
                                        currently receiving retention messaging. If you have
                                        populations on the dashboard that are outside of the
                                        retention framework and you want them to also receive this
                                        campaign, please specify this in the notes section below.
                                    </div>

                                    <div
                                        v-else-if="typeOfStudents === 'with_tag'"
                                        class="campaign-form-subitem__row"
                                    >
                                        <BaseSelectButton
                                            class="tag-operator-button-select"
                                            v-model="form.studentFilter.tagRelationship"
                                            :options="tagOperators"
                                            label="Select Tag Relationship"
                                            :invalid="!form.studentFilter.tagRelationship"
                                        />
                                        <BaseTreeSelect
                                            class="card-filter__dropdown"
                                            label="Tag Name*"
                                            placeholder="Select a tag"
                                            :modelValue="selectedTags"
                                            :options="tagsOptions"
                                            :loading="loading"
                                            :hideLabel="true"
                                            optionLabel="name"
                                            optionValue="id"
                                            optionGroupLabel="name"
                                            optionGroupValue="id"
                                            optionGroupChildren="tagsRef"
                                            selectionMode="checkbox"
                                            :selectionLimit="15"
                                            :maxSelectedLabels="1"
                                            :asyncFilter="true"
                                            :invalid="selectedTags.length === 0"
                                            @handleFilter="handleFilterTags"
                                            @loadOptionsNextPage="onTagCategoriesNextPage"
                                            @update:modelValue="handleUpdateSelectedTags"
                                        />
                                    </div>

                                    <div
                                        v-else-if="typeOfStudents === 'custom_list'"
                                        class="campaign-form-subitem"
                                    >
                                        <div class="campaign-form-subitem__row">
                                            <ChooseFileCancelable
                                                class="choose-file-container"
                                                :class="{
                                                    'student-list-not-uploaded': !isStudentListUploaded,
                                                    invalid: !isStudentListUploaded && studentIDs.length === 0
                                                }"
                                                :readAsString="false"
                                                @loaded="fileHandler"
                                                :fileUploaded="isStudentListUploaded"
                                                :origin="'request-campaign-form'"
                                                :validStudentCount="studentIDs.length"
                                                :loading="uploadingList"
                                            >
                                                <template #message>
                                                    <span class="text-bold text-primary">
                                                        Import a CSV of student email addresses for
                                                        this campaign.
                                                    </span>
                                                </template>
                                            </ChooseFileCancelable>
                                            <div
                                                class="info-panel campaign-form-subitem__row__info-panel"
                                            >
                                                Every active student on this list will be emailed
                                                when the campaign sends.<br />
                                                Download the CSV template to upload student emails
                                                <a
                                                    :href="'/chatbot-flow-builder/import-student-list-template.csv'"
                                                    download
                                                >
                                                    here
                                                </a>.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="campaign-form-item">
                                <BaseDatePicker
                                    v-model="form.flow.date"
                                    label="When Would You Like This Custom to Go Out?*"
                                    placeholder="MM/DD/YYYY HH:MM AM/PM"
                                    :showTime="true"
                                    :invalid="!form.flow.date"
                                    :minDate="new Date()"
                                />
                            </div>

                            <div class="campaign-form-item">
                                <BaseTextarea
                                    v-model="form.flow.note"
                                    label="Anything Else We Should Know?"
                                    placeholder="Share comments or instructions for the Edsights team here"
                                />
                            </div>
                            <div class="campaign-form-item">
                                <BaseButton
                                    label="Submit Campaign Request"
                                    @click="submitCampaignRequest"
                                    class="submit-button"
                                    :loading="submitingCampaign"
                                    :disabled="invalidForm"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </BaseCard>

            <BaseCard v-else class="success-card">
                <template #content>
                    <div class="success-message-container">
                        <div class="success-message-container__title">
                            <BaseIcon class="check-icon" icon="roundcheck" />
                            <h2>Campaign successfully submitted!</h2>
                        </div>
                        <p>Thank you for submitting your campaign request.</p>
                        <p>Our team will review it and reach out if we have any questions.</p>

                        <BaseButton
                            label="Submit Another Campaign"
                            @click="reloadPage"
                            class="submit-button"
                            :loading="submitingCampaign"
                        />
                    </div>
                </template>
            </BaseCard>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import {
    BaseButton,
    BaseCard,
    BaseDatePicker,
    BaseIcon,
    BaseInputText,
    BaseSelect,
    BaseSelectButton,
    BaseTreeSelect,
    BaseTextarea
} from '@edsights/ui-core';
import { CHATBOT_FLOW_STATUS } from '@/consts/chatbot-flow';
import { useRootStore } from '@/stores';
import Alerts from '@/lib/alerts';
import v2CollectionManager from '@/services/v2CollectionManager';
import { createFlow, copyChatbotFlow, adminCreateFlowFromTemplateId } from '@/api/chatbot-flow';
import ChooseFileCancelable from '@/components-deprecated/ChooseFileCancelable.vue';
import { listValidStudentIdsFromCsv } from '@/api/student-filter';
import RequestCampaignStartFrom from '@/views/RequestCampaignForm/components/RequestCampaignStartFrom.vue';
import RequestCampaignFormHeader from '@/views/RequestCampaignForm/components/RequestCampaignFormHeader.vue';
import TemplateSearch from '@/views/RequestCampaignForm/components/TemplateSearch.vue';
import TemplateCard from '@/views/RequestCampaignForm/components/TemplateCard.vue';
import * as SchoolApi from '@/api/school';
import { isCustomCampaignStartFromEnabled } from '@/lib/feature-flag';
import PastCampaignSearch from '@/views/RequestCampaignForm/components/PastCampaignSearch.vue';
import PastCampaignCard from '@/views/RequestCampaignForm/components/PastCampaignCard.vue';

const rootStore = useRootStore();

const form = ref({
    studentFilter: {},
    firstQuestion: {
        text: '',
        answerOptions: []
    },
    flow: {
        date: null,
        note: ''
    },
    template: {
        name: '',
        reusable: false
    },
    touchPoint: {
        description: 'TBD',
        isCustom: true
    }
});

const typeOfStudents = ref('');

const options = ref([
    { label: 'All Students', value: 'all' },
    { label: 'Students with a Specific Tag', value: 'with_tag' },
    { label: 'Custom List', value: 'custom_list' }
]);

const tagCategories = v2CollectionManager.create({
    listType: 'tagCategories',
    filters: {
        schoolId: rootStore.userSchoolId
    },
    errorHandlerOptions: {
        enableAlert: true,
        rethrow: false
    }
});

const selectedTags = ref([]);
const tagsOptions = ref([]);
const tagOperators = ref([
    { label: 'Match ALL selected Tags', value: 'AND' },
    { label: 'Match ANY selected tag', value: 'OR' }
]);

const loading = ref(false);
const submitingCampaign = ref(false);
const formSubmitted = ref(false);
const isStudentListUploaded = ref(false);
const uploadingList = ref(false);
const studentIDs = ref([]);
const isFirstStep = ref(true);
const startFrom = ref(null);
const chosenTemplate = ref(null);
const bindings = ref([]);
const isFlagEnabled = ref(false);
const chosenCampaign = ref(null);

const invalidForm = computed(() => {
    const areDefaultFieldsInvalid = (
        !form.value.template.name ||
        form.value.template.name.length === 0 ||
        !typeOfStudents.value ||
        (typeOfStudents.value === 'with_tag' && selectedTags.value.length === 0) ||
        (typeOfStudents.value === 'custom_list' && studentIDs.value.length === 0) ||
        !form.value.flow.date
    );

    if (startFrom.value === 'template') {
        return !chosenTemplate.value || !chosenTemplate.value.id || areDefaultFieldsInvalid;
    }

    if (startFrom.value === 'past_campaign') {
        return !chosenCampaign.value || !chosenCampaign.value.id || areDefaultFieldsInvalid;
    }

    return (
        areDefaultFieldsInvalid ||
        !form.value.firstQuestion.text ||
        form.value.firstQuestion.text.length === 0
    );
});

const loadTags = async () => {
    try {
        loading.value = true;
        await tagCategories.refresh();
        tagsOptions.value = tagCategories.list ?? [];
    } catch (error) {
        console.error('Error loading tags:', error);
        Alerts.alert({
            type: 'error',
            message: 'Failed to load tags. Please try again later.'
        });
    } finally {
        loading.value = false;
    }
};

async function onTagCategoriesNextPage() {
    if (tagCategories.pagination.next != null) {
        await tagCategories.addNextPage();
        tagsOptions.value = tagCategories.list ?? [];
    }
}

async function handleFilterTags(searchText) {
    tagCategories.filters.search = null;

    if (searchText) {
        tagCategories.filters.search = searchText;
    }

    await loadTags();
}

const createCampaignFromTemplate = async () => {
    if (!chosenTemplate.value || !chosenTemplate.value.id) {
        Alerts.alert({
            type: 'error',
            message: 'Please select a valid template to create a campaign from.'
        });
        return;
    }

    const payload = {
        schoolId: rootStore.userSchoolId,
        templateId: chosenTemplate.value.id,
        date: form.value.flow.date,
        note: form.value.flow.note,
        template: form.value.template,
        studentFilter: {
            firstTimeEngager: false,
            tagIds: form.value.studentFilter.tags ?? [],
            schoolId: rootStore.userSchoolId,
            tagRelationship: form.value.studentFilter.tagRelationship ?? null,
            studentList: studentIDs.value ?? []
        },
        errorHandlerOptions: {
            rethrow: true,
            enableAlert: true
        }
    };

    await adminCreateFlowFromTemplateId(payload);
};

const createCampaignFromPastCampaign = async () => {
    if (!chosenCampaign.value || !chosenCampaign.value.id) {
        Alerts.alert({
            type: 'error',
            message: 'Please select a valid past campaign to create a campaign from.'
        });
        return;
    }

    await copyChatbotFlow({
        id: chosenCampaign.value.id,
        overrides: {
            schoolId: rootStore.userSchoolId,
            date: form.value.flow.date,
            note: form.value.flow.note,
            template: form.value.template,
            studentFilter: {
                firstTimeEngager: false,
                tagIds: form.value.studentFilter.tags ?? [],
                schoolId: rootStore.userSchoolId,
                tagRelationship: form.value.studentFilter.tagRelationship ?? null,
                studentList: studentIDs.value ?? []
            }
        },
        errorHandlerOptions: {
            rethrow: true,
            enableAlert: true
        }
    });
};

const createChatbotFlow = async () => {
    const payload = {
        status: CHATBOT_FLOW_STATUS.DRAFT.value,
        date: form.value.flow.date,
        firstQuestion: form.value.firstQuestion,
        template: form.value.template,
        touchPoint: form.value.touchPoint,
        labels: [],
        nudge: false,
        note: form.value.flow.note,
        schoolId: rootStore.userSchoolId
    };

    payload.studentFilter = {
        firstTimeEngager: false,
        tagIds: form.value.studentFilter.tags ?? [],
        schoolId: rootStore.userSchoolId,
        tagRelationship: form.value.studentFilter.tagRelationship ?? null,
        studentList: studentIDs.value ?? []
    };

    await createFlow({
        payload,
        errorHandlerOptions: {
            rethrow: true,
            enableAlert: true
        }
    });
};

const saveFunctions = ref({
    template: createCampaignFromTemplate,
    past_campaign: createCampaignFromPastCampaign,
    scratch: createChatbotFlow
});

const submitCampaignRequest = async () => {
    try {
        if (!startFrom.value) {
            Alerts.alert({
                type: 'error',
                message: 'Please select a starting point for your campaign request.'
            });
            return;
        }

        if (invalidForm.value) {
            Alerts.alert({
                type: 'error',
                message:
                    'Please fill out all required fields before submitting the campaign request.'
            });
            return;
        }

        submitingCampaign.value = true;

        await saveFunctions.value[startFrom.value]();

        formSubmitted.value = true;
    } catch (error) {
        console.error('Error submitting campaign request:', error);
        Alerts.alert({
            type: 'error',
            message: 'Failed to submit campaign request. Please try again later.'
        });
    } finally {
        submitingCampaign.value = false;
    }
};

const handleUpdateSelectedTags = (value) => {
    selectedTags.value = value;
    const keys = Object.keys(value) ?? [];

    form.value.studentFilter.tags = keys.map((key) => Number(key));
};

const reloadPage = () => {
    window.location.reload();
};

const fileHandler = async (file) => {
    try {
        uploadingList.value = true;
        // cancel button will go through this block to clear the student list
        if (!file) {
            studentIDs.value = [];

            return;
        }

        const res = await listValidStudentIdsFromCsv(rootStore.userSchoolId, file);

        if (typeof res === 'object' && Array.isArray(res.ids)) {
            studentIDs.value = res.ids;
        }
    } catch (error) {
        console.error('Error uploading student list:', error);
        Alerts.alert({
            type: 'error',
            message: 'Failed to upload student list. Please check the file format and try again.'
        });
    } finally {
        uploadingList.value = false;
    }
};

const setStartFrom = async (value) => {
    isFirstStep.value = false;
    startFrom.value = value;

    if (value !== 'scratch') {
        await loadBindings();
    }
};

const handlePreviousStep = () => {
    isFirstStep.value = true;
    startFrom.value = null;
    chosenTemplate.value = null;
    form.value.template.id = null;
    form.value.template.name = '';
    form.value.firstQuestion.text = '';
    form.value.touchPoint.description = null;
    typeOfStudents.value = null;
};

const handleTemplateSelected = (template) => {
    form.value.template.id = template.id;
    form.value.template.name = template.name;
    form.value.firstQuestion.text = template.firstQuestion.text;
    form.value.touchPoint.description = template.touchPointDescription;
    chosenTemplate.value = template;
};

const handlePastCampaignSelected = (campaign) => {
    form.value.template.id = campaign.id;
    form.value.template.name = campaign.templateRef.name;
    form.value.touchPoint.description = campaign.touchpointRef.description;
    chosenCampaign.value = campaign;

    if (campaign.studentFilter.tagsRef.length > 0) {
        typeOfStudents.value = 'with_tag';
        form.value.studentFilter.tagRelationship = campaign.studentFilter.tagRelationship;
    } else if (campaign.studentFilter.hasStudentList) {
        typeOfStudents.value = 'custom_list';
    } else {
        typeOfStudents.value = 'all';
    }
};

const loadBindings = async () => {
    try {
        bindings.value = await SchoolApi.findTemplateBindingsBySchoolId(rootStore.userSchoolId);
    } catch (error) {
        console.error('Error loading bindings:', error);
        Alerts.alert({
            type: 'error',
            message: 'Failed to load bindings. Please try again later.'
        });
    }
};

watch(typeOfStudents, (newValue) => {
    if (newValue === 'with_tag' && tagsOptions.value.length === 0) {
        loadTags();
    }

    if (newValue !== 'custom_list') {
        isStudentListUploaded.value = false;
        studentIDs.value = [];
    }

    if (newValue !== 'with_tag') {
        form.value.studentFilter.tagRelationship = null;
        form.value.studentFilter.tags = [];
        selectedTags.value = [];
    }
});

onBeforeMount(() => {
    isFlagEnabled.value = isCustomCampaignStartFromEnabled();
    if (!isFlagEnabled.value) {
        isFirstStep.value = false;
        startFrom.value = 'scratch';
    }
});
</script>

<style scoped lang="scss">
@import '~@/styles/variables';
.request-campaign-form-view {
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &__title {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 1rem;
        color: $edsights-blue;
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1rem 2rem;
        width: 90%;
    }
}
.info-panel {
    margin: 1rem 0;
    padding: 1.5rem;
    background: #f9f9f9;
    border-left: 5px solid $edsights-blue;
    border-radius: 4px;
    position: relative;
}
.request-campaign-form-card {
    margin: 2rem;
}
.campaign-form-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    box-sizing: border-box;

    .campaign-form-item {
        :deep(.ui-core-base-textarea),
        :deep(.ui-core-base-input-text),
        :deep(.ui-core-base-date-picker),
        :deep(.ui-core-base-select) {
            width: 100%;
        }

        .campaign-form-subitem {
            margin-top: 1rem;
            &__row {
                display: flex;
                align-items: center;
                gap: 0.8rem;

                &__info-panel {
                    flex: 1;
                    font-size: 0.9rem;
                    color: #666;
                }
            }

            .choose-file-container {
                padding-left: 0;
                flex: 1;
            }
            :deep(.ui-core-base-tree-select) {
                flex: 1;
            }

            :deep(.p-togglebutton-label) {
                padding: 0.25rem 1rem;
            }
        }
    }
}
.main-form-card {
    width: 100%;
    padding: 1rem;
}
.campaign-form {
    width: 100%;
}
@media (max-width: 900px) {
    .campaign-form-subitem {
        margin-top: 1rem;

        &__row {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            &__info-panel {
                width: 100%;
            }
        }

        :deep(.ui-core-base-tree-select) {
            width: 100%;
        }

        .choose-file-container {
            width: 100%;
            padding: 0;
        }
    }
}
.submit-button {
    display: flex;
    justify-content: center;
}
.blue-bold {
    color: $edsights-blue;
    font-weight: bold;
}
.success-message-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem;
    gap: 1rem;

    &__title {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 1rem;
    }
}

.check-icon {
    :deep(.pi-check-circle) {
        color: #2ecc71;
        font-size: 3.5rem;
    }
}
.tag-operator-button-select {
    :deep(.p-togglebutton.p-togglebutton-checked),
    :deep(.p-togglebutton.p-togglebutton-checked::before) {
        background: $edsights-blue !important;
        border-color: $edsights-blue !important;
        color: white !important;
    }

    :deep(.p-togglebutton) {
        background-color: white !important;
        color: #0c0c0c;
        border: 1px solid #d1d5db !important;
        font-weight: bold !important;
    }
}
.success-card {
    margin: auto;
    max-width: 600px;
}

.request-campaign-form-view__content {
    &:has(.success-card) {
        display: flex;
        min-height: calc(100vh - 120px);
        justify-content: center;
        align-items: center;
    }
}
.student-list-not-uploaded {
    :deep(.file-box) {
        width: 100%;
        padding: 1.5rem;
    }

    :deep(.cancel-button) {
        padding: 2.5rem;
    }
}
.student-list-not-uploaded.invalid {
    :deep(.file-box) {
        border-color: red;
    }
}
</style>
